import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setTokenProvider } from '@/lib/http/apiClient';
import { isUnrecoverableAuthError } from './authErrors';

/**
 * Wires the apiClient's token provider to the Auth0 SDK once it's ready.
 * Mount once at the root inside Auth0Provider.
 *
 * If the session can no longer produce an access token (e.g. the refresh token is
 * missing/expired), we clear the stale Auth0 session and send the user back to log in,
 * rather than returning a null token and letting every request 401 into a white screen.
 *
 * Reliability: `getAccessTokenSilently()` can transiently throw (a flaky silent-auth
 * call, refresh-token contention under a burst of concurrent requests, or the brief
 * window right after login where `isAuthenticated` lags). If we returned null in those
 * moments, apiClient would send the request with NO Authorization header and the backend
 * would 401 it — even though the user is fully authenticated and holds a still-valid
 * access token. To prevent that, we cache the last known-good token and fall back to it
 * while it is unexpired, so an authenticated request is never sent token-less over a
 * momentary hiccup. Genuine expiry still flows through getAccessTokenSilently (which
 * refreshes) and genuine session loss still logs out.
 */

// Module-scoped so the cache survives the provider closure being re-created whenever the
// effect re-runs (isAuthenticated / getAccessTokenSilently changing identity).
let cachedToken: string | null = null;
let cachedExpSeconds = 0;

/** Decode a JWT's `exp` (epoch seconds); 0 if it can't be read. */
function readExpSeconds(jwt: string): number {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1])) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : 0;
  } catch {
    return 0;
  }
}

/** A cached token we can still safely use (30s skew guard). */
function validCachedToken(): string | null {
  if (cachedToken && cachedExpSeconds - 30 > Date.now() / 1000) return cachedToken;
  return null;
}

export function TokenBridge() {
  const { getAccessTokenSilently, isAuthenticated, logout } = useAuth0();
  // Guards against firing logout more than once (the token getter runs per-request).
  const loggingOut = useRef(false);

  useEffect(() => {
    setTokenProvider(async () => {
      if (!isAuthenticated) {
        // Right after login `isAuthenticated` can briefly lag while the SDK finishes
        // processing the callback; if we still hold a valid token, keep using it rather
        // than sending in-flight requests token-less.
        return validCachedToken();
      }
      try {
        const token = await getAccessTokenSilently();
        if (token) {
          cachedToken = token;
          cachedExpSeconds = readExpSeconds(token);
          return token;
        }
        return validCachedToken();
      } catch (err) {
        // Surface the real reason. Common causes: third-party cookies blocked (silent
        // auth iframe fails), audience not authorized for the SPA, or no refresh-token
        // grant (offline_access) so silent renewal has nothing to use.
        console.error('[TokenBridge] getAccessTokenSilently failed:', err);
        // A single flaky silent-auth call should not 401 an authenticated request:
        // fall back to the still-valid cached token if we have one.
        const fallback = validCachedToken();
        if (fallback) return fallback;
        if (isUnrecoverableAuthError(err) && !loggingOut.current) {
          loggingOut.current = true;
          cachedToken = null;
          cachedExpSeconds = 0;
          console.warn('[TokenBridge] unrecoverable auth error — logging out and returning to login.');
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
        return null;
      }
    });
  }, [getAccessTokenSilently, isAuthenticated, logout]);

  return null;
}
